// Waste i18n namespace (Module 05 §4). Wired into apps/web's aggregated
// `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically, and
// declared in src/i18n-completeness.ts so the VALUE gate knows which locales
// are real.
//
// `wasteEn` is authoritative; `WasteDict = typeof wasteEn` makes every locale
// parity-checked at COMPILE time (a missing or extra key fails tsc). `ru` is a
// real translation (waste.ru.ts) — the design partner's group_admin configures
// this screen in Russian. fr, zh, zh-Hant and hi live in their own
// waste.<locale>.ts files, loaded only with that locale (D-430).
//
// ENTITY NOUNS ARE NOT IN HERE. "Off-cut", "Remnant", "Bobbin" are TERMINOLOGY
// keys resolved through `useTerm()` (module 02 §2.5). The CATEGORY and CAUSE
// names are not here either, and could not be: they are tenant data
// (`name_i18n` on the row), which is exactly why the editor below shows one
// field per locale.

import type { Translated } from "../../../i18n-shape.js";
import { wasteRuDict } from "./waste.ru.js";

export const wasteEn = {
  nav: {
    aria: "Waste sections",
    settings: "Waste settings",
    // 05-B: the three data screens beside the settings editor.
    ledger: "Waste ledger",
    shifts: "Shift scoreboard",
    // S13 — the ranked "why did we scrap" board.
    pareto: "Scrap Pareto",
    remnants: "Remnants",
  },

  common: {
    retry: "Retry",
    requestId: "Request ID",
    // The label in front of a problem `title` on a write-failure note (task
    // #105; mutation-failure-note.tsx). Labelled AS the server's because the
    // title is wire English (D-022) and arrives untranslated on every locale's
    // screen — the label is what stops it reading as our own broken sentence.
    serverSaid: "Refused: “{{title}}”",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    edit: "Edit",
    create: "Create",
    activate: "Turn on",
    deactivate: "Turn off",
    close: "Close",
    loading: "Loading…",
    prev: "Previous",
    next: "Next",
  },

  // A REFUSAL, said as a refusal. Not an error card: an error card offers Retry,
  // and a Retry that can never succeed teaches the reader that the platform is
  // flaky when it is doing exactly what it was configured to do.
  access: {
    title: "Not your screen",
    area: "Your role has no waste screens. Ask a group administrator if you need them.",
    ledger:
      "Your role does not read the waste ledger. A group administrator can grant it if this is part of your job.",
    registry:
      "Your role does not manage the remnant registry. A group administrator can grant it if this is part of your job.",
  },

  // A PAUSED READ, not a slow one. The request is held until the network is
  // back; a loading skeleton here would shimmer for as long as the cut lasts.
  offline: {
    title: "No network",
    body: "This device is offline. The screen fills itself in as soon as the connection returns — nothing was lost.",
  },

  // The §2.3 source enum (closed set — the wire stays canonical English codes;
  // these are the labels S2's filter and rows render).
  source: {
    floor_report: "Floor report",
    qc_disposition: "QC disposition",
    calibration_variance: "Calibration variance",
    cut_offcut: "Cut / off-cut",
    count_variance: "Count variance",
    byproduct_output: "By-product",
    regrind_loss: "Regrind loss",
    manual: "Office entry",
  },

  // §2.6 closed enums.
  remnantKind: {
    wire_remnant: "Wire remnant",
    cable_offcut: "Cable off-cut",
    sheet_offcut: "Sheet off-cut",
  },
  remnantStatus: {
    available: "Available",
    reserved: "Reserved",
    consumed: "Consumed",
    sold: "Sold",
    scrapped: "Scrapped",
  },

  // S2 — the waste ledger (/waste/events).
  events: {
    title: "Waste ledger",
    filters: "Filters ({{n}})",
    filtersTitle: "Filters",
    filtersDone: "Done",
    filterRemove: "Remove {{label}}",
    exportCsv: "Export CSV",
    // The export walks the FULL filtered result server-side (GM-week audit:
    // it used to serialize only the pages already on screen). The three
    // sentences below are that walk's voice: progress while it runs, the
    // honesty-cap alert when the file holds the first N rows OF MORE, and the
    // no-file promise when the walk dies mid-way. `{{n}}` not `{{count}}` —
    // house rule, the i18next plural machinery is not what these want.
    exporting: "Exporting… {{n}} rows so far",
    exportCapped:
      "Exported the first {{n}} rows — the ledger holds more. Narrow the date range and export the rest separately.",
    exportFailed: "Could not export the ledger — no file was saved.",
    facility: "Facility",
    allFacilities: "All facilities",
    from: "From",
    to: "To",
    category: "Waste type",
    allCategories: "All waste types",
    allCauses: "All causes",
    source: "Source",
    allSources: "All sources",
    disposition: "Disposition",
    allDispositions: "All dispositions",
    posted: "Posted",
    postedAny: "Posted or not",
    postedYes: "Posted",
    postedNo: "Not posted",
    // 05-B-2 — the §4 S2 work-centre / shift / WO filter controls.
    workCenter: "Work centre",
    allWorkCenters: "All work centres",
    shift: "Shift",
    allShifts: "Any shift",
    pickFacilityFirst: "Pick a facility first",
    wo: "Work order",
    woPlaceholder: "Paste a work order id",
    woInvalid: "Not a work order id — the filter is off until it is one.",
    error: "Could not load the waste ledger",
    emptyTitle: "No waste events yet",
    emptyBody: "Waste shows here as soon as the floor, QC or a machine books it.",
    tableCaption: "Waste events, newest first",
    colNo: "No.",
    colWhen: "When",
    colCategory: "Waste type",
    colCause: "Reason",
    colQty: "Qty",
    colCost: "Cost",
    colSource: "Source",
    colDisposition: "Disposition",
    reversedChip: "Reversed",
    // The OTHER half of the pair. Without it the reversal — which sorts first,
    // being newest — read as a second, real scrap event.
    reversalChip: "Reversal",
    loadMore: "Show more",
    drawerShift: "Shift",
    noShift: "No shift — office entry",
    // D-102's RETRO-ATTRIBUTION, and the whole point of it is WHICH shift.
    // This chip named none for as long as it existed: it read the id as a
    // boolean and threw it away, so the anti-gaming mechanic — "created on the
    // day shift, found on the night shift" — was half invisible to the GM it
    // was built for. `handedOffFrom` survives as the honest fallback for an
    // origin no board can name (the two-date scoreboard join reaches yesterday,
    // and an origin older than that resolves to nothing but its id).
    drawerOrigin: "Caused on",
    handedOffFromShift: "Handed off from {{shift}}",
    handedOffFrom: "Handed off from another shift",
    drawerWo: "Work order",
    drawerPhotos: "Photos",
    drawerNotes: "Notes",
    // D-225: the server used to write these sentences in English into `notes`,
    // in an append-only ledger. It now records WHICH row this one corrects and
    // which half of the pair it is; the sentence is composed here, so a Russian
    // storekeeper reads Russian and a better phrasing reaches historical rows too.
    drawerCorrection: "Correction",
    correctionRestatement: "Restated from {{eventNo}}",
    correctionAddition: "Added on top of {{eventNo}}",
    decideLabel: "Decide what happens to it",
    decideSave: "Set disposition",
    decideFailed: "Could not set the disposition — it may already be decided.",
    reverseReason: "Why is this wrong?",
    reverseConfirm: "Create the reversal",
    reverseOpen: "Reverse…",
    reverseFailed: "Could not reverse the event.",
    reversedNote:
      "This event is part of a reversal pair. The pair cancels to zero in every total; record a fresh event for the corrected fact.",
  },

  // S4 — the shift waste scoreboard (/waste/shifts, ?kiosk=1 wall mode).
  shifts: {
    title: "Shift waste scoreboard",
    kioskBadge: "Wall mode — refreshes every minute",
    date: "Date",
    error: "Could not load the scoreboard",
    emptyTitle: "No waste on this day",
    emptyBody: "Nothing was booked for this facility and date.",
    unattributed: "No shift",
    unattributedNote:
      "Office and backfilled entries carry no shift on purpose — money entered days later must not land on whoever happened to be working.",
    cost: "Waste cost",
    events: "Events",
    handedOff: "Handed-off cost",
    topCategories: "Top waste types today",
    week: "Last 7 days",
    // One cell of the seven-day strip has three possible claims, and they used
    // to share an unlabelled slot: money, an event count (when the money is
    // redacted), and a day with no bucket at all. A bare "0" for the third read
    // as a total; a bare "12" for the second read as tenge.
    weekNone: "None",
    weekEvents: "{{count}} events",
    drillLedger: "Open these events in the ledger",
    drillShift: "Open this shift in the ledger",
    // Kiosk mode hides both pickers by design; the wall still has to say which
    // plant and which day it is showing.
    kioskContext: "{{facility}} · {{date}}",
    kioskFacilityUnknown: "Facility not set",
  },

  // S13 — the scrap Pareto (/waste/pareto). The GM's Monday question: why did
  // we scrap, ranked by what it cost, over a range that matches a P&L month.
  pareto: {
    title: "Scrap Pareto",
    intro: "Waste ranked by cost. The causes above 80% matter most.",
    groupBy: "Rank by",
    byCause: "Cause",
    byCategory: "Waste type",
    from: "From",
    to: "To",
    presetThisMonth: "This month",
    presetLastMonth: "Last month",
    // A backwards range is a typo. Saying "no waste in this period" for one
    // would tell a director their plant scrapped nothing.
    rangeError: "The start date is after the end date — no period is selected.",
    error: "Could not load the Pareto",
    emptyTitle: "No waste booked in this period",
    emptyBody: "Nothing was recorded for this facility between these dates.",
    // The two list headings. Money names its currency; quantity names its unit,
    // because metres and kilograms are different lists and never one total.
    basisMoney: "Ranked by cost ({{currency}})",
    basisQty: "Ranked by quantity ({{uom}})",
    // WHY the basis changed, said where the reader meets it: without this the
    // screen looks broken rather than honest.
    redactedNote:
      "Your role can't see waste costs, so this ranks by quantity, one unit of measure at a time.",
    colRank: "#",
    colCause: "Cause",
    colCategory: "Waste type",
    colCost: "Cost",
    colQty: "Quantity",
    colShare: "Share",
    colCumulative: "Running total",
    colEvents: "Events",
    colVolume: "Volume",
    // A REAL header, not a blank spacer: the phone card names every cell by its
    // header, and a column whose accessible name is the empty string is a link
    // a screen-reader user meets with no idea what it opens.
    colDrill: "Ledger",
    drill: "Open in ledger",
    tableCaption: "Waste causes ranked, with each one's share of the period",
    // Scrap booked with no cause is a FINDING — the unexplained fraction of the
    // month — not an empty cell.
    noCause: "No cause recorded",
    noCategory: "No waste type recorded",
    // 0% of nothing is a claim. When the period's total is zero the shares are
    // not computable and the screen says which of the two zeroes it is.
    shareUnknown: "—",
    zeroCost: "No cost is recorded for this scrap, so it can't be ranked by money.",
    zeroQty: "No quantity is recorded against this scrap.",
    vitalFew: "The top {{count}} account for 80% of this period.",
  },

  // S5 — the remnant registry (/waste/remnants).
  remnants: {
    title: "Remnant registry",
    search: "Search",
    searchHint: "Remnant number…",
    // 05-B-2 — the ITEM half of §4's "item picker + required length" bar.
    item: "Item",
    itemHint: "Type the full SKU…",
    itemApplied: "Filtering by {{sku}}",
    itemNoMatch: "No item with that exact SKU — the filter is off until one matches.",
    kind: "Kind",
    anyKind: "Any kind",
    status: "Status",
    anyStatus: "Any status",
    minLength: "Min length (m)",
    maxLength: "Max length (m)",
    error: "Could not load the remnant registry",
    emptyTitle: "No remnants on the books",
    emptyBody: "Off-cuts appear here when a piece is cut, or when the floor registers a leftover.",
    tableCaption: "Remnants, longest first",
    colNo: "No.",
    colKind: "Kind",
    colLength: "Length",
    colDiameter: "Ø",
    colClass: "Price class",
    colStatus: "Status",
    colAge: "Registered",
    colActions: "Actions",
    unclassed: "Not classed",
    actions: "Actions",
    // PER-VERB failure sentences (task #105). One "The action was refused"
    // string for all four lifecycle writes told the operator neither WHICH
    // action the server refused nor why; each verb now has its own sentence,
    // and the shared MutationFailureNote appends the server's own title and
    // the request id after it.
    reserveFailed: "Could not reserve the piece — someone may have taken it first.",
    releaseFailed: "Could not release the reservation — it may already be gone.",
    reclassFailed: "Could not re-class the piece.",
    scrapFailed: "Could not scrap the piece.",
    reserve: "Reserve",
    reserveFor: "Reserve for",
    reserveWo: "Work order",
    reserveSo: "Sales order line",
    reserveRef: "Reference ID",
    release: "Release",
    reclass: "Re-class",
    reclassPick: "Pick a class",
    reclassTo: "New price class",
    scrap: "Scrap",
    scrapReason: "Why is it being scrapped?",
  },

  // S8/S9 — the floor capture screens (station context).
  floor: {
    captureTitle: "Record waste",
    remnantTitle: "Register a remnant",
    backToStation: "Back to the station",
    back: "Back",
    noCatalogTitle: "No waste types on this device yet",
    noCatalogBody: "Open this screen once while online so the lists are stored for offline use.",
    noJobTitle: "No job at this station",
    noJobBody:
      "Waste is booked against the job that produced it. When a job is on the queue here, it appears above.",
    qtyLabel: "How much? ({{uom}})",
    lengthLabel: "Length (m)",
    diameterLabel: "Diameter (mm) — optional",
    locationLabel: "Where does the piece go?",
    locationPick: "Pick a bin",
    noteLabel: "Note — optional",
    addPhoto: "Add a photo",
    // Never "photo(s)": a slashed plural is a string that was not written for
    // either number, and the Russian beside it is already phrased correctly.
    photosPicked: "Photos attached: {{n}}",
    photoRefused: "That photo cannot be used — too large or not an image.",
    saveCapture: "Save the waste",
    saveRemnant: "Register the piece",
    saving: "Saving…",
    captureAnother: "Record another",
    remnantExistingHint: "Piece already has a label?",
    remnantScanLink: "Scan it to update length or location.",
    savedApplied: "Saved.",
    savedQueued: "Saved on this device — it will sync when the network returns.",
    savedConflicted: "This clashes with what is already recorded. A supervisor should review it.",
    savedFailed: "Not saved: it was refused. Tell your supervisor.",
    savedSuperseded: "Already recorded elsewhere.",
  },

  locale: {
    en: "English",
    ru: "Russian",
    fr: "French",
    zh: "Chinese (Simplified)",
    "zh-Hant": "Chinese (Traditional)",
    hi: "Hindi",
  },

  // The §2.1 disposition vocabulary. These ARE ours to translate (a closed
  // enum), unlike the category names beside them.
  disposition: {
    pending: "Not decided yet",
    reuse: "Reuse",
    regrind: "Regrind",
    sell: "Sell",
    dispose: "Dispose",
  },

  settings: {
    title: "Waste settings",
    intro: "What the floor picks from when it records waste.",
    readOnly: "You can see these settings. Changing them is a group administrator's job.",
    settingsReadOnly: "Only a group administrator can change these numbers.",
    tabsAria: "Waste settings sections",
    tab: {
      categories: "Waste types",
      causes: "Reasons",
      classes: "Off-cut price classes",
      matcher: "Matching & tolerances",
    },
    loading: "Loading…",
    saved: "Saved.",

    colCode: "Code",
    colName: "Name",
    colDisposition: "What usually happens to it",
    colFlags: "Capture",
    colOwner: "Comes from",
    colActions: "Actions",
    colAppliesTo: "Used for",
    colBlame: "Points at",
    colDiscount: "Discount",
    colBand: "Length band",
    colSortOrder: "Order",
    colFloorPolicy: "Selling below cost",

    fromPack: "Industry pack",
    fromPlatform: "Built in",
    tenantOwned: "Yours",
    customize: "Customize",
    rowMenu: "More actions for {{code}}",
    otherLanguages: "Other languages",
    search: "Search waste types and reasons",
    searchPlaceholder: "Code or name",
    noMatch: "Nothing matches this search.",
    pageRange: "Your own: {{from}}–{{to}} of {{total}}",
    customizeHint:
      "This row is kept up to date by the industry pack. Once you change it, it becomes yours and pack updates stop touching it.",
    noTranslation: "English",
    noTranslationHint: "No translation for your language yet — showing the English name.",
    optionalLocale: "Leave empty if you have no translation",
    codeIsForever:
      "The code is permanent — the floor's scrap reports point at it. The name can change any time.",

    flagDimensions: "Asks for a length",
    flagNoDimensions: "No length",
    flagCause: "Asks for a reason",
    flagNoCause: "No reason",
    flagRecoverable: "Can be recovered",
    flagNotRecoverable: "Pure loss",
    allCategories: "All waste types",
    blamesMachine: "The machine",
    blamesSupplier: "The supplier",
    blamesNobody: "Nothing in particular",

    // §2.2 sort_order. The reason this control exists, in the operator's own
    // terms: without it the floor's second tap is ordered by machine code —
    // English letters — in front of a Russian-speaking operator.
    sortOrderHint: "Lower numbers show first on the floor. Leave empty to keep the current order.",
    sortOrderInvalid: "The order must be a whole number between 0 and 32767.",

    // §2.2 category_ids. Empty means ALL, which is the opposite of what an
    // empty tick list looks like, so both states are spelled out.
    appliesToAllHint: "Nothing ticked — this reason can be used for every waste type.",
    appliesToHint:
      "Only the ticked waste types offer this reason. Untick them all to allow it everywhere.",

    // §2.7 floor_price_policy.
    floorPolicy: {
      above_cost: "Needs approval",
      none: "Allowed",
    },
    floorPolicyHint:
      "‘Needs approval’ means a piece cannot be sold for less than it cost to make until someone approves it.",
    // §2.7 band uniqueness — the documented way out of a band clash.
    deactivateFreesBand:
      "Two classes cannot claim the same length. Turning a class off frees its band for another one.",

    addCategory: "Add a waste type",
    addCause: "Add a reason",
    addClass: "Add a price class",

    bandHint:
      "A piece is priced by the band its length falls in. Bands cannot overlap — a 100 m off-cut must have exactly one price.",
    bandNone: "Chosen by hand",
    bandBelow: "Under {{max}} m",
    bandAbove: "{{min}} m and over",
    bandRange: "{{min}}–{{max}} m",
    bandMin: "From (m)",
    bandMax: "Up to, not including (m)",
    pct: "{{value}}%",

    emptyCategoriesTitle: "No waste types yet",
    emptyCategoriesBody:
      "Waste types are what the floor taps first when something is scrapped. Install an industry pack, or add your own.",
    emptyCausesTitle: "No reasons yet",
    emptyCausesBody:
      "Reasons are the second tap on the floor — why it happened. Add the ones your line actually sees.",
    emptyClassesTitle: "No price classes yet",
    emptyClassesBody:
      "Price classes set the discount on off-cuts you sell. Add one per length band you price differently.",

    errorCatalog: "Could not load the waste lists",
    errorClasses: "Could not load the price classes",
    errorSettings: "Could not load the settings",
    noSettingsAccessTitle: "Not your settings to see",
    noSettingsAccessBody:
      "Matching and tolerance numbers are visible to administrators and planners.",

    field: {
      exact_tolerance_m: "Exact-match tolerance (m)",
      max_overage_pct: "Most a piece may overshoot (%)",
      reservation_ttl_hours: "How long a hold lasts (hours)",
      variance_tolerance_default_pct: "Waste over plan before it is flagged (%)",
      wire_diameter_tolerance_mm: "Same-wire tolerance (mm)",
      min_remnant_track_length_m: "Shortest piece worth keeping (m)",
      recovery_target_pct_default: "Default recovery target (%)",
    },
    fieldHint: {
      exact_tolerance_m: "A piece this close to the length asked for counts as an exact match.",
      max_overage_pct: "A suggestion that wastes more than this is not offered.",
      reservation_ttl_hours: "After this, a held piece goes back on the shelf on its own.",
      variance_tolerance_default_pct: "Used when a specific target does not say otherwise.",
      wire_diameter_tolerance_mm: "Two wires within this are treated as the same wire.",
      min_remnant_track_length_m: "Shorter cut-offs are waste, not stock. From the industry pack.",
      recovery_target_pct_default: "The share of waste you aim to recover. From the industry pack.",
    },
    // D-069. The flag is one-way — there is no un-customize — so the warning
    // has to arrive BEFORE the first change, not as a receipt after it.
    packDefaults: {
      pristine:
        "The last two numbers came from your industry pack, and pack updates still correct them. Change either one and that stops — for good.",
      customized:
        "You have set your own value for one of the pack's two numbers. Pack updates no longer change them.",
    },
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type WasteDict = Translated<typeof wasteEn>;

// ru is TRANSLATED (see waste.ru.ts) and pinned by tools/ci/i18n-parity.ts,
// which fails if `ru` ever regresses to en values. The other locales import
// their own waste.<locale>.ts directly, so this module never bundles them.
export const wasteRu: WasteDict = wasteRuDict;
